import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import { useForm, Controller } from 'react-hook-form';
import DataTable from '../../components/common/DataTable';
import {
  useCostRules, useCreateCostRule, useUpdateCostRule, useDeleteCostRule,
  useTrailers, useDrivers,
  useCostCategories, useCreateCostCategory, useUpdateCostCategory, useDeleteCostCategory,
  useSuppliers, useCreateSupplier, useUpdateSupplier, useDeleteSupplier,
} from '../../hooks/useApi';
import { api } from '../../services/api';
import type { CostRule, CostRuleType, CostCategoryItem, Supplier } from '../../types';

const ruleTypeLabels: Record<CostRuleType, string> = {
  PER_KM: 'Per km',
  PER_DAY: 'Per dag',
  PER_TRIP: 'Per tur',
  PER_HOUR: 'Per time',
  PER_INVOICE_LINE: 'Per varelinje',
  FIXED_MONTHLY: 'Fast månedlig',
};

const ruleTypeUnitLabels: Record<CostRuleType, string> = {
  PER_KM: 'DKK/km',
  PER_DAY: 'DKK/dag',
  PER_TRIP: 'DKK/tur',
  PER_HOUR: 'DKK/time',
  PER_INVOICE_LINE: 'DKK/linje',
  FIXED_MONTHLY: 'DKK/md.',
};

const chipColorOptions = [
  { value: 'default', label: 'Grå' },
  { value: 'primary', label: 'Blå' },
  { value: 'secondary', label: 'Lilla' },
  { value: 'success', label: 'Grøn' },
  { value: 'warning', label: 'Orange' },
  { value: 'error', label: 'Rød' },
  { value: 'info', label: 'Lyseblå' },
];

interface CostRuleFormData {
  name: string;
  description: string;
  ruleType: CostRuleType;
  category: string;
  rate: string;
  trailerIds: string[];
  driverIds: string[];
  productNumbers: string;
  supplierId: string;
  isActive: boolean;
}

interface CategoryFormData {
  key: string;
  name: string;
  color: string;
}

export default function CostRuleList() {
  const [formOpen, setFormOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState<CostRule | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<CostRule | null>(null);

  // Supplier management state
  const [supplierFormOpen, setSupplierFormOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [deleteSupplierDialogOpen, setDeleteSupplierDialogOpen] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState<Supplier | null>(null);
  const [deleteSupplierError, setDeleteSupplierError] = useState<string | null>(null);

  // Export dialog state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  // Category management state
  const [categoryFormOpen, setCategoryFormOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CostCategoryItem | null>(null);
  const [deleteCategoryDialogOpen, setDeleteCategoryDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<CostCategoryItem | null>(null);
  const [deleteCategoryError, setDeleteCategoryError] = useState<string | null>(null);

  const { data: costRules, isLoading, error } = useCostRules();
  const { data: trailers } = useTrailers();
  const { data: drivers } = useDrivers();
  const { data: categories } = useCostCategories();
  const { data: suppliers } = useSuppliers();
  const createRule = useCreateCostRule();
  const updateRule = useUpdateCostRule();
  const deleteRule = useDeleteCostRule();
  const createCategory = useCreateCostCategory();
  const updateCategory = useUpdateCostCategory();
  const deleteCategory = useDeleteCostCategory();
  const createSupplier = useCreateSupplier();
  const updateSupplier = useUpdateSupplier();
  const deleteSupplierMut = useDeleteSupplier();

  // Dynamic category lookups
  const categoryLabels = useMemo(() => {
    const map: Record<string, string> = {};
    categories?.forEach(c => { map[c.key] = c.name; });
    return map;
  }, [categories]);

  const categoryColors = useMemo(() => {
    const map: Record<string, string> = {};
    categories?.forEach(c => { map[c.key] = c.color; });
    return map;
  }, [categories]);

  const supplierLabels = useMemo(() => {
    const map: Record<string, string> = {};
    suppliers?.forEach(s => { map[s.id] = s.name; });
    return map;
  }, [suppliers]);

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<CostRuleFormData>({
    defaultValues: {
      name: '',
      description: '',
      ruleType: 'PER_KM',
      category: 'OTHER',
      rate: '',
      trailerIds: [],
      driverIds: [],
      productNumbers: '',
      isActive: true,
    },
  });

  const selectedRuleType = watch('ruleType');

  const handleCreate = () => {
    setSelectedRule(null);
    reset({
      name: '',
      description: '',
      ruleType: 'PER_KM',
      category: categories?.[0]?.key || 'OTHER',
      rate: '',
      trailerIds: [],
      driverIds: [],
      productNumbers: '',
      supplierId: '',
      isActive: true,
    });
    setFormOpen(true);
  };

  const handleEdit = (rule: CostRule) => {
    setSelectedRule(rule);
    reset({
      name: rule.name,
      description: rule.description || '',
      ruleType: rule.ruleType,
      category: rule.category,
      rate: String(rule.rate),
      trailerIds: rule.trailerIds || [],
      driverIds: rule.driverIds || [],
      productNumbers: rule.productNumbers.join(', '),
      supplierId: rule.supplierId || '',
      isActive: rule.isActive,
    });
    setFormOpen(true);
  };

  // Category handlers
  const handleCreateCategory = () => {
    setEditingCategory(null);
    setCategoryFormOpen(true);
  };

  const handleEditCategory = (cat: CostCategoryItem) => {
    setEditingCategory(cat);
    setCategoryFormOpen(true);
  };

  const handleDeleteCategory = (cat: CostCategoryItem) => {
    setCategoryToDelete(cat);
    setDeleteCategoryError(null);
    setDeleteCategoryDialogOpen(true);
  };

  const confirmDeleteCategory = async () => {
    if (!categoryToDelete) return;
    try {
      await deleteCategory.mutateAsync(categoryToDelete.id);
      setDeleteCategoryDialogOpen(false);
      setCategoryToDelete(null);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Kunne ikke slette kategorien';
      // Extract message from axios error response
      const axiosMsg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setDeleteCategoryError(axiosMsg || msg);
    }
  };

  const handleCategoryFormSubmit = async (data: CategoryFormData) => {
    try {
      if (editingCategory) {
        await updateCategory.mutateAsync({ id: editingCategory.id, data: { name: data.name, color: data.color } });
      } else {
        await createCategory.mutateAsync({ key: data.key, name: data.name, color: data.color });
      }
      setCategoryFormOpen(false);
    } catch {
      // Error handled by mutation
    }
  };

  // Supplier handlers
  const handleCreateSupplier = () => {
    setEditingSupplier(null);
    setSupplierFormOpen(true);
  };

  const handleEditSupplier = (sup: Supplier) => {
    setEditingSupplier(sup);
    setSupplierFormOpen(true);
  };

  const handleDeleteSupplier = (sup: Supplier) => {
    setSupplierToDelete(sup);
    setDeleteSupplierError(null);
    setDeleteSupplierDialogOpen(true);
  };

  const confirmDeleteSupplier = async () => {
    if (!supplierToDelete) return;
    try {
      await deleteSupplierMut.mutateAsync(supplierToDelete.id);
      setDeleteSupplierDialogOpen(false);
      setSupplierToDelete(null);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Kunne ikke slette leverandøren';
      const axiosMsg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setDeleteSupplierError(axiosMsg || msg);
    }
  };

  const handleSupplierFormSubmit = async (data: { name: string; cvr: string }) => {
    try {
      if (editingSupplier) {
        await updateSupplier.mutateAsync({ id: editingSupplier.id, data: { name: data.name, cvr: data.cvr || null } });
      } else {
        await createSupplier.mutateAsync({ name: data.name, cvr: data.cvr || null });
      }
      setSupplierFormOpen(false);
    } catch {
      // Error handled by mutation
    }
  };

  const handleDelete = (rule: CostRule) => {
    setRuleToDelete(rule);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (ruleToDelete) {
      await deleteRule.mutateAsync(ruleToDelete.id);
      setDeleteDialogOpen(false);
      setRuleToDelete(null);
    }
  };

  const onSubmit = async (data: CostRuleFormData) => {
    const rateNum = parseFloat(data.rate);
    if (isNaN(rateNum) || rateNum <= 0) return;

    const payload = {
      name: data.name,
      description: data.description || null,
      ruleType: data.ruleType,
      category: data.category,
      rate: rateNum,
      trailerIds: data.trailerIds,
      driverIds: data.driverIds,
      productNumbers: data.ruleType === 'PER_INVOICE_LINE'
        ? data.productNumbers.split(',').map(s => s.trim()).filter(Boolean)
        : [],
      supplierId: data.supplierId || null,
      isActive: data.isActive,
    };

    try {
      if (selectedRule) {
        await updateRule.mutateAsync({ id: selectedRule.id, data: payload });
      } else {
        await createRule.mutateAsync(payload);
      }
      setFormOpen(false);
    } catch {
      // Error handled by mutation
    }
  };

  const getTrailerLabel = (id: string) => {
    const trailer = trailers?.find(t => t.id === id);
    return trailer ? (trailer.alias || trailer.number) : id.slice(0, 8);
  };

  const getDriverLabel = (id: string) => {
    const driver = drivers?.find(d => d.id === id);
    return driver ? `${driver.firstName} ${driver.lastName}` : id.slice(0, 8);
  };

  const getScopeLabel = (rule: CostRule) => {
    const parts: string[] = [];
    if (rule.trailerIds?.length > 0) {
      parts.push(rule.trailerIds.length === 1
        ? getTrailerLabel(rule.trailerIds[0])
        : `${rule.trailerIds.length} køretøjer`
      );
    } else {
      parts.push('Alle køretøjer');
    }
    if (rule.driverIds?.length > 0) {
      parts.push(rule.driverIds.length === 1
        ? getDriverLabel(rule.driverIds[0])
        : `${rule.driverIds.length} chauffører`
      );
    }
    return parts.join(' · ');
  };

  const columns = [
    {
      id: 'name',
      label: 'Navn',
      minWidth: 150,
    },
    {
      id: 'ruleType',
      label: 'Type',
      minWidth: 120,
      format: (value: unknown) =>
        ruleTypeLabels[value as CostRuleType] || String(value),
    },
    {
      id: 'category',
      label: 'Kategori',
      minWidth: 120,
      format: (value: unknown) => (
        <Chip
          label={categoryLabels[value as string] || String(value)}
          size="small"
          color={(categoryColors[value as string] || 'default') as 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'}
        />
      ),
    },
    {
      id: 'rate',
      label: 'Sats',
      minWidth: 120,
      format: (value: unknown, row: CostRule) =>
        `${Number(value).toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 4 })} ${ruleTypeUnitLabels[row.ruleType]}`,
    },
    {
      id: 'scope',
      label: 'Omfang',
      minWidth: 160,
      format: (_: unknown, row: CostRule) => getScopeLabel(row),
    },
    {
      id: 'supplierId',
      label: 'Leverandør',
      minWidth: 120,
      format: (value: unknown) => value ? (supplierLabels[value as string] || '-') : '-',
    },
    {
      id: 'isActive',
      label: 'Status',
      minWidth: 80,
      format: (value: unknown) => (
        <Chip
          label={value ? 'Aktiv' : 'Inaktiv'}
          size="small"
          color={value ? 'success' : 'default'}
          variant={value ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      id: 'actions',
      label: '',
      align: 'right' as const,
      minWidth: 100,
      sortable: false,
      format: (_: unknown, row: CostRule) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
          <Tooltip title="Rediger">
            <IconButton size="small" onClick={() => handleEdit(row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Slet">
            <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (error) {
    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>Omkostninger</Typography>
        <Alert severity="error">Kunne ikke hente omkostningsregler.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Omkostninger</Typography>

      {/* Categories Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={600}>Kategorier</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={handleCreateCategory}>
              Tilføj kategori
            </Button>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {categories?.map(cat => (
              <Chip
                key={cat.id}
                label={cat.name}
                color={(cat.color || 'default') as 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'}
                onDelete={() => handleDeleteCategory(cat)}
                onClick={() => handleEditCategory(cat)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
            {(!categories || categories.length === 0) && (
              <Typography variant="body2" color="text.secondary">Indlæser kategorier...</Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Suppliers Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={600}>Leverandører</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={handleCreateSupplier}>
              Tilføj leverandør
            </Button>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {suppliers?.map(sup => (
              <Chip
                key={sup.id}
                label={sup.name + (sup.cvr ? ` (${sup.cvr})` : '')}
                onDelete={() => handleDeleteSupplier(sup)}
                onClick={() => handleEditSupplier(sup)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
            {(!suppliers || suppliers.length === 0) && (
              <Typography variant="body2" color="text.secondary">Ingen leverandører oprettet endnu.</Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Cost Rules Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>Omkostningsregler</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" startIcon={<DownloadIcon />} onClick={() => setExportDialogOpen(true)}>
            Eksporter
          </Button>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleCreate}>
            Opret regel
          </Button>
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : costRules && costRules.length > 0 ? (
        <DataTable
          columns={columns}
          data={costRules}
          rowKey="id"
          searchPlaceholder="Søg regel..."
        />
      ) : (
        <Alert severity="info">
          Ingen omkostningsregler oprettet endnu. Opret regler for at automatisere dit økonomiske overblik.
        </Alert>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {selectedRule ? 'Rediger regel' : 'Opret regel'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {(createRule.error || updateRule.error) && (
                <Alert severity="error">
                  {(createRule.error as Error)?.message || (updateRule.error as Error)?.message || 'Der opstod en fejl'}
                </Alert>
              )}

              <TextField
                label="Navn"
                {...register('name', { required: 'Navn er påkrævet' })}
                error={!!errors.name}
                helperText={errors.name?.message}
                placeholder="F.eks. Dækslitage, Leasing, Bompenge"
              />

              <TextField
                label="Beskrivelse (valgfri)"
                {...register('description')}
                multiline
                rows={2}
              />

              <Controller
                name="ruleType"
                control={control}
                rules={{ required: 'Type er påkrævet' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.ruleType}>
                    <InputLabel>Type</InputLabel>
                    <Select {...field} label="Type">
                      {(Object.keys(ruleTypeLabels) as CostRuleType[]).map(type => (
                        <MenuItem key={type} value={type}>
                          {ruleTypeLabels[type]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />

              <Controller
                name="category"
                control={control}
                rules={{ required: 'Kategori er påkrævet' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.category}>
                    <InputLabel>Kategori</InputLabel>
                    <Select {...field} label="Kategori">
                      {categories?.map(cat => (
                        <MenuItem key={cat.key} value={cat.key}>
                          <Chip
                            label={cat.name}
                            size="small"
                            color={(cat.color || 'default') as 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'}
                            sx={{ mr: 1 }}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />

              <Controller
                name="supplierId"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Leverandør (valgfri)</InputLabel>
                    <Select {...field} label="Leverandør (valgfri)">
                      <MenuItem value="">
                        <em>Ingen leverandør</em>
                      </MenuItem>
                      {suppliers?.map(sup => (
                        <MenuItem key={sup.id} value={sup.id}>
                          {sup.name}{sup.cvr ? ` (${sup.cvr})` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />

              <TextField
                label={`Sats (${ruleTypeUnitLabels[selectedRuleType] || 'DKK'})`}
                {...register('rate', {
                  required: 'Sats er påkrævet',
                  validate: (v) => {
                    const num = parseFloat(v);
                    if (isNaN(num) || num <= 0) return 'Sats skal være et positivt tal';
                    return true;
                  },
                })}
                error={!!errors.rate}
                helperText={errors.rate?.message}
                type="number"
                inputProps={{ step: '0.01', min: '0' }}
              />

              {/* Multi-select trailers */}
              <Controller
                name="trailerIds"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Køretøjer</InputLabel>
                    <Select
                      multiple
                      value={field.value}
                      onChange={field.onChange}
                      input={<OutlinedInput label="Køretøjer" />}
                      renderValue={(selected) => {
                        if (selected.length === 0) return 'Alle køretøjer';
                        return selected.map(id => getTrailerLabel(id)).join(', ');
                      }}
                    >
                      {trailers?.map(trailer => (
                        <MenuItem key={trailer.id} value={trailer.id}>
                          <Checkbox size="small" checked={field.value.includes(trailer.id)} />
                          <ListItemText primary={`${trailer.alias || trailer.number} (${trailer.type})`} />
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                      Ingen valgt = alle køretøjer
                    </Typography>
                  </FormControl>
                )}
              />

              {/* Multi-select drivers */}
              <Controller
                name="driverIds"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Chauffører</InputLabel>
                    <Select
                      multiple
                      value={field.value}
                      onChange={field.onChange}
                      input={<OutlinedInput label="Chauffører" />}
                      renderValue={(selected) => {
                        if (selected.length === 0) return 'Alle chauffører';
                        return selected.map(id => getDriverLabel(id)).join(', ');
                      }}
                    >
                      {drivers?.map(driver => (
                        <MenuItem key={driver.id} value={driver.id}>
                          <Checkbox size="small" checked={field.value.includes(driver.id)} />
                          <ListItemText primary={`${driver.firstName} ${driver.lastName}`} />
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                      Ingen valgt = alle chauffører
                    </Typography>
                  </FormControl>
                )}
              />

              {selectedRuleType === 'PER_INVOICE_LINE' && (
                <TextField
                  label="Produktnumre (kommasepareret)"
                  {...register('productNumbers')}
                  helperText="Angiv produktnumre fra DORA, adskilt med komma"
                  placeholder="5001, 5002, 5003"
                />
              )}

              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={field.value} onChange={field.onChange} />}
                    label="Aktiv"
                  />
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFormOpen(false)}>Annuller</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={createRule.isPending || updateRule.isPending}
            >
              {createRule.isPending || updateRule.isPending ? (
                <CircularProgress size={24} />
              ) : selectedRule ? (
                'Gem ændringer'
              ) : (
                'Opret'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Rule Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Slet regel?</DialogTitle>
        <DialogContent>
          <Typography>
            Er du sikker på du vil slette <strong>{ruleToDelete?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Annuller</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Slet
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Form Dialog */}
      <CategoryFormDialog
        open={categoryFormOpen}
        onClose={() => setCategoryFormOpen(false)}
        onSubmit={handleCategoryFormSubmit}
        editingCategory={editingCategory}
        isPending={createCategory.isPending || updateCategory.isPending}
        error={createCategory.error || updateCategory.error}
      />

      {/* Delete Category Confirmation Dialog */}
      <Dialog open={deleteCategoryDialogOpen} onClose={() => setDeleteCategoryDialogOpen(false)}>
        <DialogTitle>Slet kategori?</DialogTitle>
        <DialogContent>
          <Typography>
            Er du sikker på du vil slette kategorien <strong>{categoryToDelete?.name}</strong>?
          </Typography>
          {deleteCategoryError && (
            <Alert severity="error" sx={{ mt: 1 }}>{deleteCategoryError}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteCategoryDialogOpen(false)}>Annuller</Button>
          <Button onClick={confirmDeleteCategory} color="error" variant="contained" disabled={deleteCategory.isPending}>
            Slet
          </Button>
        </DialogActions>
      </Dialog>

      {/* Supplier Form Dialog */}
      <SupplierFormDialog
        open={supplierFormOpen}
        onClose={() => setSupplierFormOpen(false)}
        onSubmit={handleSupplierFormSubmit}
        editingSupplier={editingSupplier}
        isPending={createSupplier.isPending || updateSupplier.isPending}
        error={createSupplier.error || updateSupplier.error}
      />

      {/* Delete Supplier Confirmation Dialog */}
      <Dialog open={deleteSupplierDialogOpen} onClose={() => setDeleteSupplierDialogOpen(false)}>
        <DialogTitle>Slet leverandør?</DialogTitle>
        <DialogContent>
          <Typography>
            Er du sikker på du vil slette leverandøren <strong>{supplierToDelete?.name}</strong>?
          </Typography>
          {deleteSupplierError && (
            <Alert severity="error" sx={{ mt: 1 }}>{deleteSupplierError}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteSupplierDialogOpen(false)}>Annuller</Button>
          <Button onClick={confirmDeleteSupplier} color="error" variant="contained" disabled={deleteSupplierMut.isPending}>
            Slet
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cost Export Dialog */}
      <CostExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        categories={categories || []}
        suppliers={suppliers || []}
      />
    </Box>
  );
}

// Separate component for category form dialog to isolate useForm
function CategoryFormDialog({
  open,
  onClose,
  onSubmit,
  editingCategory,
  isPending,
  error,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CategoryFormData) => Promise<void>;
  editingCategory: CostCategoryItem | null;
  isPending: boolean;
  error: Error | null;
}) {
  const { register, handleSubmit, control, reset, formState: { errors } } = useForm<CategoryFormData>({
    defaultValues: { key: '', name: '', color: 'default' },
  });

  // Reset form when dialog opens
  const handleEnter = () => {
    if (editingCategory) {
      reset({ key: editingCategory.key, name: editingCategory.name, color: editingCategory.color });
    } else {
      reset({ key: '', name: '', color: 'default' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth TransitionProps={{ onEnter: handleEnter }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>{editingCategory ? 'Rediger kategori' : 'Opret kategori'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {error && (
              <Alert severity="error">{(error as Error)?.message || 'Der opstod en fejl'}</Alert>
            )}

            {!editingCategory && (
              <TextField
                label="Nøgle"
                {...register('key', {
                  required: 'Nøgle er påkrævet',
                  pattern: { value: /^[A-Z][A-Z0-9_]*$/, message: 'Kun store bogstaver, tal og underscore' },
                })}
                error={!!errors.key}
                helperText={errors.key?.message || 'F.eks. TOLL, DRIVER_SALARY'}
                placeholder="TOLL"
              />
            )}

            <TextField
              label="Visningsnavn"
              {...register('name', { required: 'Navn er påkrævet' })}
              error={!!errors.name}
              helperText={errors.name?.message}
              placeholder="F.eks. Vejafgifter"
            />

            <Controller
              name="color"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Farve</InputLabel>
                  <Select {...field} label="Farve">
                    {chipColorOptions.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        <Chip label={opt.label} size="small" color={opt.value as 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'} sx={{ mr: 1 }} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Annuller</Button>
          <Button type="submit" variant="contained" disabled={isPending}>
            {isPending ? <CircularProgress size={24} /> : editingCategory ? 'Gem' : 'Opret'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// Supplier form dialog
function SupplierFormDialog({
  open,
  onClose,
  onSubmit,
  editingSupplier,
  isPending,
  error,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; cvr: string }) => Promise<void>;
  editingSupplier: Supplier | null;
  isPending: boolean;
  error: Error | null;
}) {
  const { register, handleSubmit, reset, formState: { errors } } = useForm<{ name: string; cvr: string }>({
    defaultValues: { name: '', cvr: '' },
  });

  const handleEnter = () => {
    if (editingSupplier) {
      reset({ name: editingSupplier.name, cvr: editingSupplier.cvr || '' });
    } else {
      reset({ name: '', cvr: '' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth TransitionProps={{ onEnter: handleEnter }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>{editingSupplier ? 'Rediger leverandør' : 'Opret leverandør'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {error && (
              <Alert severity="error">{(error as Error)?.message || 'Der opstod en fejl'}</Alert>
            )}

            <TextField
              label="Navn"
              {...register('name', { required: 'Navn er påkrævet' })}
              error={!!errors.name}
              helperText={errors.name?.message}
              placeholder="F.eks. Ekstern Vognmand A/S"
            />

            <TextField
              label="CVR-nummer (valgfri)"
              {...register('cvr')}
              placeholder="12345678"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Annuller</Button>
          <Button type="submit" variant="contained" disabled={isPending}>
            {isPending ? <CircularProgress size={24} /> : editingSupplier ? 'Gem' : 'Opret'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// Cost export dialog
function CostExportDialog({
  open,
  onClose,
  categories,
  suppliers,
}: {
  open: boolean;
  onClose: () => void;
  categories: CostCategoryItem[];
  suppliers: Supplier[];
}) {
  const now = new Date();
  const firstOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(today);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState<string[]>([]);
  const [format, setFormat] = useState<'excel' | 'pdf' | 'csv'>('excel');
  const [downloading, setDownloading] = useState(false);

  const handleExport = async () => {
    setDownloading(true);
    try {
      const cats = selectedCategories.length > 0 ? selectedCategories.join(',') : undefined;
      const sups = selectedSuppliers.length > 0 ? selectedSuppliers.join(',') : undefined;
      if (format === 'excel') {
        await api.downloadCostReportExcel(from, to, cats, sups);
      } else if (format === 'pdf') {
        await api.downloadCostReportPdf(from, to, cats, sups);
      } else {
        await api.downloadCostReportCsv(from, to, cats, sups);
      }
      onClose();
    } catch {
      // Error handled silently
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Eksporter omkostningsrapport</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Fra"
              type="date"
              value={from}
              onChange={e => setFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Til"
              type="date"
              value={to}
              onChange={e => setTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>

          <FormControl fullWidth>
            <InputLabel>Kategorier</InputLabel>
            <Select
              multiple
              value={selectedCategories}
              onChange={e => setSelectedCategories(e.target.value as string[])}
              input={<OutlinedInput label="Kategorier" />}
              renderValue={(selected) =>
                selected.length === 0
                  ? 'Alle kategorier'
                  : selected.map(k => categories.find(c => c.key === k)?.name || k).join(', ')
              }
            >
              {categories.map(cat => (
                <MenuItem key={cat.key} value={cat.key}>
                  <Checkbox size="small" checked={selectedCategories.includes(cat.key)} />
                  <ListItemText primary={cat.name} />
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
              Ingen valgt = alle kategorier
            </Typography>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Leverandører</InputLabel>
            <Select
              multiple
              value={selectedSuppliers}
              onChange={e => setSelectedSuppliers(e.target.value as string[])}
              input={<OutlinedInput label="Leverandører" />}
              renderValue={(selected) =>
                selected.length === 0
                  ? 'Alle leverandører'
                  : selected.map(id => suppliers.find(s => s.id === id)?.name || id).join(', ')
              }
            >
              {suppliers.map(sup => (
                <MenuItem key={sup.id} value={sup.id}>
                  <Checkbox size="small" checked={selectedSuppliers.includes(sup.id)} />
                  <ListItemText primary={sup.name} />
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
              Ingen valgt = alle leverandører
            </Typography>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Format</InputLabel>
            <Select
              value={format}
              onChange={e => setFormat(e.target.value as 'excel' | 'pdf' | 'csv')}
              label="Format"
            >
              <MenuItem value="excel">Excel (.xlsx)</MenuItem>
              <MenuItem value="pdf">PDF (.pdf)</MenuItem>
              <MenuItem value="csv">CSV (.csv)</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuller</Button>
        <Button
          onClick={handleExport}
          variant="contained"
          disabled={downloading || !from || !to}
          startIcon={downloading ? <CircularProgress size={20} /> : <DownloadIcon />}
        >
          {downloading ? 'Eksporterer...' : 'Eksporter'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
